const HttpStatus = require('http-status-codes'),
  assert = require('assert'),
  makeEnum = require('./enum'),
  {wix} = require('./generated/proto-generated'),
  trailers = require('./trailers'),
  protoRuntime = require('@wix/wix-proto-runtime-toolkit');

const ErrorCode = {
  UNKNOWN: -100,
  INVALID_SESSION: -101,
  RPC_ERROR: -102,
  GATEKEEPER_ACCESS_DENIED: -103,
  HEALTH_TEST_FAILED: -104,
  SESSION_REQUIRED: -105,
  BAD_CSRF_TOKEN: -106,
  UNHANDLED_REJECTION: -107,
  THROTTLED: -108,
  GATEKEEPER_METASITE_NOT_FOUND: -109,
  GRPC_ERROR: -110,
  UNSUPPORTED_BROWSER: 407
};

class WixBaseError extends Error {
  constructor(msg, {cause, errorCode = ErrorCode.UNKNOWN, httpStatusCode = HttpStatus.INTERNAL_SERVER_ERROR, name} = {
    errorCode: ErrorCode.UNKNOWN, httpStatusCode: HttpStatus.INTERNAL_SERVER_ERROR
  }) {
    super(msg);
    assert(Number.isInteger(errorCode), 'errorCode must be provided and be a valid integer');
    assert.doesNotThrow(() => HttpStatus.getStatusText(httpStatusCode), 'HTTP status must be valid');
    assert(!msg || typeof (msg) === 'string', 'message is mandatory');
    this.name = name || this.constructor.name;
    this._errorCode = errorCode;
    this._httpStatus = httpStatusCode;
    addCauseIfValid(this, cause);
    generateStackTrace(this);
  }

  get cause() {
    return this._cause;
  }

  get errorCode() {
    return this._errorCode;
  }

  get httpStatusCode() {
    return this._httpStatus;
  }
}

class WixUnsupportedBrowserError extends WixBaseError {
  constructor(data) {
    super('Unsupported Browser', {errorCode: ErrorCode.UNSUPPORTED_BROWSER, httpStatusCode: HttpStatus.OK});
    this.details = {errorCode: 'unsupportedBrowser', data};
  }
}

class WixError extends WixBaseError {
  constructor(msg, cause) {
    super(msg, {cause});
  }
}

class WixSystemError extends WixBaseError {
  constructor(msg, opts) {
    super(msg, opts);
  }
}

class WixBusinessError extends WixBaseError {
  constructor(msg, opts) {
    super(msg, opts);
    this._exposeMessage = true;
    this._escapeHtml = (opts || {}).escapeHtml !== false;
  }

  get escapeHtml() {
    return this._escapeHtml;
  }
}

// NOTE: this is legacy class. It will be removed in future.
// wix-express-proto expects these errors to be thrown from Protobuf-defined services
// when WNP_EMULATE_GRPC_ERRORS feature toggle !== true
class GrpcError extends WixBaseError {
  constructor(code, message, details, cause) {
    super(message, {cause});
    this._errorCode = ErrorCode.GRPC_ERROR;
    this.code = code;
    this.details = details;
    this._exposeMessage = true;
  }
}

const GrpcStatus = makeEnum({
  OK: 0,
  CANCELLED: 1,
  UNKNOWN: 2,
  INVALID_ARGUMENT: 3,
  DEADLINE_EXCEEDED: 4,
  NOT_FOUND: 5,
  ALREADY_EXISTS: 6,
  PERMISSION_DENIED: 7,
  RESOURCE_EXHAUSTED: 8,
  FAILED_PRECONDITION: 9,
  ABORTED: 10,
  OUT_OF_RANGE: 11,
  UNIMPLEMENTED: 12,
  INTERNAL: 13,
  UNAVAILABLE: 14,
  DATA_LOSS: 15,
  UNAUTHENTICATED: 16
});

function combineMessage(code, message) {
  const codeStr = GrpcStatus.asString(code);
  if (message && !message.startsWith(codeStr)) {
    return `${codeStr}: ${message}`;
  } else if (message) {
    return message;
  } else {
    return codeStr;
  }
}

const {GRPC_STATUS_DETAILS} = trailers;

class GrpcStatusError extends WixBaseError {
  constructor(status, message, metadata, cause, name) {
    const code = GrpcStatus.asNumber(status) || GrpcStatus.UNKNOWN;
    super(combineMessage(code, message), {cause, name});
    this.code = code;
    if (metadata) {
      this.metadata = metadata;
    }
    if (this.details) {
      this._exposeMessage = true;
    }
  }

  get details() {
    return this.metadata && this.metadata[GRPC_STATUS_DETAILS];
  }

  static applicationError(code, message, applicationError, name) {
    return new GrpcStatusError(code, message, wrapErrorDetails({applicationError}), null, name);
  }

  static validationError(code, message, validationError) {
    return new GrpcStatusError(code, message, wrapErrorDetails({validationError}), null, 'GrpcValidationError');
  }
}

function wrapErrorDetails(details) {
  const errors = wix.api.Details.verify(details, protoRuntime);
  if (!errors.isEmpty()) {
    throw new TypeError(`Invalid error details: "${errors}"`);
  }
  return {[GRPC_STATUS_DETAILS]: details};
}

function makeErrorClass(additionalProperties) {

  return (errorCode = ErrorCode.UNKNOWN, httpStatus = HttpStatus.INTERNAL_SERVER_ERROR) => {

    assert(Number.isInteger(errorCode), 'errorCode must be provided and be a valid integer');
    assert.doesNotThrow(() => HttpStatus.getStatusText(httpStatus), 'HTTP status must be valid');

    return class extends Error {

      constructor(msg, cause) {
        super(msg);
        assert(!msg || typeof (msg) === 'string', 'message is mandatory');
        this.name = this.constructor.name;
        Object.assign(this, additionalProperties);
        addCauseIfValid(this, cause);
        generateStackTrace(this);
      }

      get cause() {
        return this._cause;
      }

      get errorCode() {
        return errorCode;
      }

      get httpStatusCode() {
        return httpStatus;
      }
    };
  };
}

function addCauseIfValid(current, cause) {
  if (cause) {
    if (cause instanceof Error) {
      current._cause = cause;
    } else if (typeof cause === 'string') {
      current._cause = new Error(cause);
    }
  }
}

function generateStackTrace(current) {
  Error.captureStackTrace(current, current.constructor);
  if (current.cause) {
    current.stack = current.stack + '\nCaused By: ' + (current.cause.stack || current.cause.toString());
  }
}

function extractMetadata(error) {

  const meta = {
    name: error.name,
    errorCode: error.errorCode || ErrorCode.UNKNOWN,
    httpStatusCode: error.httpStatusCode || error.status || HttpStatus.INTERNAL_SERVER_ERROR,
    escapeHtml: error.escapeHtml === true
  };

  if (error._exposeMessage === true || error.expose === true) {
    meta.message = error.message;
  }

  if (typeof error.details !== 'undefined') {
    meta.details = error.details;
  }

  return meta;
}

function isBusinessError(error) {
  return error._exposeMessage !== undefined && error._exposeMessage === true;
}

const wixBusinessError = makeErrorClass({'_exposeMessage': true});

const wixSystemError = makeErrorClass({});

class ThrottledError extends wixSystemError(ErrorCode.THROTTLED, HttpStatus.SERVICE_UNAVAILABLE) {
}

module.exports = {
  WixError,
  wixBusinessError,
  WixBusinessError,
  wixSystemError,
  WixSystemError,
  WixUnsupportedBrowserError,
  HttpStatus,
  ErrorCode,
  extractMetadata,
  isBusinessError,
  GrpcStatusError,
  GrpcStatus,
  GrpcError,
  ThrottledError,
  wix,
  trailers
};
